import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { scaleIn, staggerContainer, fadeInUp } from '../utils/animations';
import './AuthPages.css';

function LoginPage() {
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleBtnRef = useRef(null);

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const from = location.state?.from?.pathname || '/';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
      setLoading(true);

    try {
      const result = await login({
        identifier: formData.identifier,
        username: formData.identifier,
        password: formData.password,
      });

      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!googleClientId || !googleBtnRef.current) return undefined;

    let cancelled = false;
    let scriptEl = null;
    let loadListener = null;

    const handleGoogleCredential = async (response) => {
      if (!response?.credential) {
        setError('Google login failed. Please try again.');
        return;
      }

      setError('');
      setGoogleLoading(true);
      const result = await loginWithGoogle(response.credential);
      setGoogleLoading(false);

      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Google login failed. Please try again.');
      }
    };

    const initGoogleButton = () => {
      if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
      googleBtnRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleCredential
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'continue_with',
        width: 320
      });
    };

    if (window.google?.accounts?.id) {
      initGoogleButton();
    } else {
      scriptEl = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.src = 'https://accounts.google.com/gsi/client';
        scriptEl.async = true;
        scriptEl.defer = true;
        document.body.appendChild(scriptEl);
      }
      loadListener = initGoogleButton;
      scriptEl.addEventListener('load', loadListener);
    }

    return () => {
      cancelled = true;
      if (scriptEl && loadListener) {
        scriptEl.removeEventListener('load', loadListener);
      }
    };
  }, [googleClientId, loginWithGoogle, navigate, from]);

  return (
    <div className="auth-page">
      <div className="auth-container">
        <motion.div
          className="auth-card"
          variants={scaleIn}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={staggerContainer} initial="hidden" animate="visible">
            <motion.h1 className="auth-title" variants={fadeInUp}>Welcome Back</motion.h1>
            <motion.p className="auth-subtitle" variants={fadeInUp}>Login to your account</motion.p>

            <AnimatePresence>
              {error && (
                <motion.div
                  className="auth-error"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form onSubmit={handleSubmit} className="auth-form" variants={fadeInUp}>
              <div className="form-group form-group-floating">
                <input
                  type="text"
                  id="identifier"
                  name="identifier"
                  value={formData.identifier}
                  onChange={handleChange}
                  required
                  placeholder=" "
                />
                <label htmlFor="identifier">Email or Username</label>
              </div>

              <div className="form-group form-group-floating">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder=" "
                />
                <label htmlFor="password">Password</label>
                <div style={{ textAlign: 'right', marginTop: '5px' }}>
                  <Link to="/forgot-password" className="forgot-password-link">
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                className="auth-button"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </motion.form>

            {googleClientId && (
              <motion.div className="auth-google-block" variants={fadeInUp}>
                <div className="auth-google-label">
                  or continue with
                </div>
                <div
                  ref={googleBtnRef}
                  className="auth-google-button-wrap"
                  style={{ opacity: googleLoading ? 0.5 : 1, pointerEvents: googleLoading ? 'none' : 'auto' }}
                />
              </motion.div>
            )}

            <motion.div className="auth-footer" variants={fadeInUp}>
              <p>
                Don't have an account?{' '}
                <Link to="/register" className="auth-link">
                  Register here
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default LoginPage;
